import * as React from 'react';

import { IMaintenanceConfig } from 'src/maintenance.config';
import { formatMaintenanceTime } from 'src/utils/maintenance';

interface IMaintenancePageProps {
  config: IMaintenanceConfig;
}

const MaintenancePage: React.FC<IMaintenancePageProps> = ({ config }) => {
  const formattedTime = formatMaintenanceTime(config.startTime);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.iconBox}>
          <svg
            style={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6-3.3 3.3-1.6-1.6a1 1 0 0 0-1.4 0l-6 6a1 1 0 0 0 1.4 1.4L11 13l1.6 1.6a1 1 0 0 0 1.4 0l4-4 1.6 1.6a1 1 0 0 0 1.7-.7V6a1 1 0 0 0-1-1h-5.3a1 1 0 0 0-.7 1.7l.4-.4z"
              fill="#FFFFFF"
            />
          </svg>
        </div>

        <h1 style={styles.title}>We're currently undergoing maintenance</h1>

        <p style={styles.message}>Please check back shortly</p>

        <div style={styles.infoCards}>
          {formattedTime && (
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>STARTED AT</div>
              <div style={styles.infoValue}>{formattedTime}</div>
            </div>
          )}
          {config.estimatedDuration && (
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>EST. DURATION</div>
              <div style={styles.infoValue}>{config.estimatedDuration}</div>
            </div>
          )}
        </div>

        {config.contactEmail && (
          <p style={styles.contact}>
            For urgent issues, contact{' '}
            <a href={`mailto:${config.contactEmail}`} style={styles.contactLink}>
              {config.contactEmail}
            </a>
          </p>
        )}

        <button style={styles.refreshButton} onClick={handleRefresh}>
          Refresh Page
        </button>
      </div>
    </div>
  );
};

/**
 * Self-contained styles — this component renders outside the
 * Provider/ThemeProvider tree, so no JSS or design tokens are available.
 * Values are hardcoded equivalents of the design-system tokens.
 */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #F5F7FA 0%, #E8ECF1 100%)',
    fontFamily: "'Open Sans', sans-serif",
    padding: 24,
  },
  container: {
    textAlign: 'center' as const,
    maxWidth: 480,
    width: '100%',
  },
  iconBox: {
    width: 80,
    height: 80,
    background: 'linear-gradient(135deg, #FF8C00, #FF6B00)',
    borderRadius: 20,
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(255,107,0,0.25)',
  },
  icon: {
    width: 36,
    height: 36,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111111',
    margin: '0 0 8px',
    fontFamily: "'Open Sans', sans-serif",
  },
  message: {
    fontSize: 15,
    color: '#666666',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  infoCards: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    justifyContent: 'center',
  },
  infoCard: {
    flex: 1,
    maxWidth: 200,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  infoLabel: {
    fontSize: 11,
    color: '#999999',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333333',
  },
  contact: {
    fontSize: 13,
    color: '#888888',
    margin: '0 0 16px',
  },
  contactLink: {
    color: '#1890FF',
    textDecoration: 'none',
  },
  refreshButton: {
    background: '#1890FF',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    padding: '10px 24px',
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 500,
    fontFamily: "'Open Sans', sans-serif",
    boxShadow: '0 2px 8px rgba(24,144,255,0.3)',
  },
};

export default MaintenancePage;
